import LaunchIcon from "@mui/icons-material/Launch";
import type { SxProps, Theme } from "@mui/material";
import { Autocomplete, Link, Stack, TextField } from "@mui/material";
import type { FC, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import type { CustomerListItem } from "../../customers/customersApi";
import { useCustomers } from "../../customers/useCustomers";

interface Props {
  readonly customerId: string | null;
  readonly onChange: (customerId: string | null, customer: CustomerListItem | null) => void;
  readonly size?: "small" | "medium";
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly disableClearable?: boolean;
  readonly showLink?: boolean;
  readonly helperText?: ReactNode;
  readonly sx?: SxProps<Theme>;
}

/** Customer picker that narrows the options to customers whose name contains the typed text. */
const CustomerSelect: FC<Props> = ({
  customerId,
  onChange,
  size,
  disabled,
  required,
  disableClearable,
  showLink,
  helperText,
  sx,
}) => {
  const { customers } = useCustomers();
  const selectedCustomer = customers.find((customer) => customer.id === customerId) ?? null;
  const autocomplete = (
    <Autocomplete
      options={customers}
      value={selectedCustomer}
      getOptionKey={(customer) => customer.id}
      getOptionLabel={(customer) => customer.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      fullWidth
      size={size}
      disabled={disabled}
      disableClearable={disableClearable}
      sx={sx}
      onChange={(_, customer) => onChange(customer?.id ?? null, customer)}
      renderInput={(params) => <TextField {...params} label="Klient" required={required} helperText={helperText} />}
    />
  );
  if (!showLink) {
    return autocomplete;
  }
  return (
    <Stack direction="row" sx={{ width: "100%", height: "fit-content" }}>
      {autocomplete}
      {customerId && (
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <Link component={RouterLink} title="Idź do klienta" color="inherit" to={`/customers/${customerId}`}>
            <LaunchIcon />
          </Link>
        </Stack>
      )}
    </Stack>
  );
};

export default CustomerSelect;
